import React from 'react';
import UiIcon from '../../components/ui/UiIcon';
import { Link } from 'react-router-dom';
import useContentIndex from '../../hooks/useContentIndex';
import { socialLinks } from '../../data/siteData';
import './HomePage.css';

const formatDate = (date) => new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(new Date(`${date}T00:00:00`));
const sections = [
  { title: 'Projects', detail: 'Projects and builds', state: 'Active', to: '/work' },
  { title: 'Blog', detail: 'Research and personal essays', state: 'Filed', to: '/blog' },
  { title: 'Profile', detail: 'About and current focus', state: 'Current', to: '/about' },
  { title: 'Archive', detail: 'Every published observation', state: 'Indexed', to: '/archive' },
];

const interests = [
  { label: '01 / Programming', title: 'Software and utilities.', body: 'Desktop tools, Python utilities, and web interfaces, with source code and notes on how they work.', to: '/work', link: 'Explore the projects' },
  { label: '02 / Research', title: 'AI, privacy, and digital tools.', body: 'Practical research, personal essays, and observations from using technology in everyday work.', to: '/blog', link: 'Read the notes' },
  { label: '03 / Experiments', title: 'Ideas put into practice.', body: 'Small prototypes, interface studies, and practical tests that help me understand an idea by working with it.', to: '/about', link: 'More about me' },
];

const HomePage = () => {
  const { items, loading } = useContentIndex();
  const published = items
    .filter((item) => item.status === 'published')
    .sort((a, b) => new Date(b.updated || b.date) - new Date(a.updated || a.date));
  const featuredThought = published[0];
  const recent = published.filter((item) => item.slug !== featuredThought?.slug).slice(0, 3);
  const connections = socialLinks.filter((link) => ['github'].includes(link.id));

  return <article className="record-page overview-record">
    <header className="record-heading overview-heading">
      <div><p className="record-kicker">Overview / personal index</p><h1>Ty0x7</h1><p>Software, research, and independent projects.</p></div>
      <dl className="record-id-block"><div><dt>Record ID</dt><dd>TY—PUBLIC—01</dd></div><div><dt>Location</dt><dd>Canada</dd></div><div><dt>Filed notes</dt><dd>{loading ? 'Retrieving…' : String(published.length).padStart(2, '0')}</dd></div></dl>
    </header>

    <section className="overview-intro" aria-label="Introduction">
      <span className="record-label">Inside the folder</span>
      <p>My projects, research, and writing in one place. Browse the source code, read about my experience with AI and privacy, or open Connections for my public links.</p>
    </section>

    <section className="overview-grid">
      <div className="identity-sheet">
        {featuredThought ? <Link className="latest-thought-slip" to={`/blog/${featuredThought.slug}`}>
          <i className="latest-thought-slip__clip" aria-hidden="true" />
          <span className="record-label">Latest thought / OBS—{String(featuredThought.id).padStart(3, '0')}</span>
          <strong>{featuredThought.title}</strong>
          <p>{featuredThought.summary}</p>
          <small>Recorded — {formatDate(featuredThought.updated || featuredThought.date)} <b>Open note <UiIcon name="right" /></b></small>
        </Link> : <div><span className="record-label">Latest thought</span><p>{loading ? 'Retrieving the latest note…' : 'No published note is currently filed.'}</p></div>}
        <span className="identity-signature">a note from the archive</span>
      </div>
      <aside className="current-focus"><span className="record-label">Index</span>{sections.map((section, index) => <Link to={section.to} key={section.to}><span>0{index + 1}</span><div><strong>{section.title}</strong><small>{section.detail}</small></div><i>{section.state}</i></Link>)}</aside>
    </section>

    <section className="overview-interests" aria-label="Areas of interest">
      {interests.map((interest) => <Link to={interest.to} key={interest.label}>
        <span className="record-label">{interest.label}</span><h2>{interest.title}</h2><p>{interest.body}</p><span className="overview-interests__link">{interest.link} <i><UiIcon name="right" /></i></span>
      </Link>)}
    </section>

    <section className="overview-bottom overview-bottom--index">
      <div className="recent-records"><span className="record-label">Recently filed</span>{recent.map((item) => <Link to={`/blog/${item.slug}`} key={item.id}><span>OBS—{String(item.id).padStart(3, '0')}</span><strong>{item.title}</strong><i>{formatDate(item.updated || item.date)}</i></Link>)}<Link className="recent-records__all" to="/archive"><span>INDEX</span><strong>Browse the complete archive</strong><i><UiIcon name="right" /></i></Link></div>
      <aside className="overview-connections"><span className="record-label">Elsewhere</span><p>Source code, experiments, and ongoing work.</p>{connections.map((link) => <a href={link.href} target="_blank" rel="noopener noreferrer" key={link.id}><strong>{link.label}</strong><span>{link.handle}</span><i><UiIcon name="external" /></i></a>)}</aside>
    </section>
  </article>;
};

export default HomePage;
